import { createEntityStore } from './createEntityStore';

// 1. Import APIs
import {
  ALL_REGIONS,
  AutomationMessageApi, ClientExecutiveApi, CountryApi, CourseApi,
  CurriculumApi, CustomFieldApi, FacilityApi,
  FlagDefinitionApi, HolidayApi, InstructorApi, KlassApi, LanguageApi,
  PartnerApi, PipelineKlassApi, PrivateCourseApi, RedHatRegionApi, SavedFilterApi,
  TrainingSpecialistStrategicApi
} from '@app/api';
import {
  AUTOMATION_MESSAGE_STORE_PAGE_SIZE, HOLIDAY_ALL_REGIONS_STORE_PAGE_SIZE, HOLIDAY_STORE_PAGE_SIZE,
  KLASS_STORE_PAGE_SIZE
} from '@app/utils/config';

// 2. Import Ingestors (We only need these for entities with nested relational data).
// They are pure: this module is where the dictionaries they resolve against are read from,
// which is also what keeps ingestionEngine.ts free of any reference back to here.
import { Holiday } from '@app/model/Holiday';
import {
  ingestClientExecutives, ingestHolidays, ingestInstructors,
  ingestKlasses, ingestPartners, ingestPipelineKlasses, ingestTss
} from './ingestionEngine';

// ============================================================================
// TIER 1: BASE DICTIONARIES (No dependencies, tiny memory footprint. TTL = 24h)
// ============================================================================
const LONG_TTL = 1440; // 24 hours in minutes

export const useCountryStore = createEntityStore(CountryApi, (d) => d, undefined, LONG_TTL);
export const useLanguageStore = createEntityStore(LanguageApi, (d) => d, undefined, LONG_TTL);
export const useRedHatRegionStore = createEntityStore(RedHatRegionApi, (d) => d, undefined, LONG_TTL);
export const useCourseStore = createEntityStore(CourseApi, (d) => d, undefined, LONG_TTL);
export const usePrivateCourseStore = createEntityStore(PrivateCourseApi, (d) => d, undefined, LONG_TTL);
export const useFacilityStore = createEntityStore(FacilityApi, (d) => d, undefined, LONG_TTL);
export const useCurriculumStore = createEntityStore(CurriculumApi, (d) => d, undefined, LONG_TTL);

// ============================================================================
// TIER 2: STANDARD ENTITIES (No dependencies. TTL = 15min)
// ============================================================================
export const useAutomationMessageStore = createEntityStore(
  AutomationMessageApi,
  (d) => d,
  undefined,
  15,
  // A log rather than reference data, so it outgrew ENTITY_STORE_PAGE_SIZE on its own schedule and
  // has the class window's size instead. See AUTOMATION_MESSAGE_STORE_PAGE_SIZE. Nothing preloads
  // it: the Automation Messages page is the only view that subscribes, so the rows arrive when it
  // is opened and are dropped again 15 minutes after it is left.
  { size: AUTOMATION_MESSAGE_STORE_PAGE_SIZE }
);
export const useCustomFieldStore = createEntityStore(CustomFieldApi, (d) => d);
export const useFlagDefinitionStore = createEntityStore(FlagDefinitionApi, (d) => d, undefined);
export const useSavedFilterStore = createEntityStore(SavedFilterApi, (d) => d, undefined);

// ============================================================================
// TIER 2: INTERMEDIATE ENTITIES (Depend on Tier 1 dictionaries)
// ============================================================================

export const useClientExecutiveStore = createEntityStore(
  ClientExecutiveApi,
  (raw) => ingestClientExecutives(raw, { redHatRegions: useRedHatRegionStore.getState().items }),
  async () => { await useRedHatRegionStore.getState().loadAllOrThrow(); }
);

export const usePartnerStore = createEntityStore(
  PartnerApi,
  (raw) => ingestPartners(raw, { clientExecutives: useClientExecutiveStore.getState().items }),
  async () => { await useClientExecutiveStore.getState().loadAllOrThrow(); }
);

export const hydrateHolidays = (raw: Holiday[]) =>
  ingestHolidays(raw, { countries: useCountryStore.getState().items });

/**
 * The same ingestion, keeping only the days a country as a whole observes.
 *
 * Also used straight by the Calendar, which fetches a country's holidays outside the store. The
 * filter is what makes "nationwide" a property of this store rather than of the request that
 * happened to fill it: a regional holiday arriving by any other route - a WebSocket push, the
 * Calendar's per-country fetch, a service that starts sending regions unasked - is dropped here
 * instead of appearing in an overlay nobody asked to see subdivisions in.
 */
export const hydrateNationwideHolidays = (raw: Holiday[]) =>
  hydrateHolidays(raw).filter(holiday => !holiday.region);

/**
 * Holidays as everything but the administration screen wants them: nationwide only, which is
 * what the endpoints answer with when no `region` is asked for.
 */
export const useHolidayStore = createEntityStore(
  HolidayApi,
  hydrateNationwideHolidays,
  async () => { await useCountryStore.getState().loadAllOrThrow(); },
  15,
  // One row per holiday per country per year adds up faster than any other reference data here,
  // and had already passed ENTITY_STORE_PAGE_SIZE. See HOLIDAY_STORE_PAGE_SIZE for how long this
  // holds and what would settle it.
  { size: HOLIDAY_STORE_PAGE_SIZE }
);

/**
 * Every holiday, including each subdivision's own - the Holidays administration screen's store,
 * and nothing else's.
 *
 * Its own store rather than a parameter on the one above, because the two collections are wanted
 * in different places at once and one cache cannot hold both: the screen manages regional
 * holidays so it has to see them, and the Calendar draws an overlay that would gain some fifty
 * Spanish days from the same rows. Nothing preloads it; the rows arrive when the screen is opened
 * and are dropped again 15 minutes after it is left.
 */
export const useHolidayAllRegionsStore = createEntityStore(
  HolidayApi,
  hydrateHolidays,
  async () => { await useCountryStore.getState().loadAllOrThrow(); },
  15,
  { size: HOLIDAY_ALL_REGIONS_STORE_PAGE_SIZE, region: ALL_REGIONS }
);

export const useTrainingSpecialistStrategicStore = createEntityStore(
  TrainingSpecialistStrategicApi,
  (raw) => ingestTss(raw, {
    countries: useCountryStore.getState().items,
    redHatRegions: useRedHatRegionStore.getState().items,
  }),
  async () => {
    await Promise.all([
      useCountryStore.getState().loadAllOrThrow(),
      useRedHatRegionStore.getState().loadAllOrThrow()
    ]);
  }
);

// ============================================================================
// TIER 3: HEAVY ENTITIES (Depend on multiple tiers. Shorter TTL = 15m)
// ============================================================================

export const useInstructorStore = createEntityStore(
  InstructorApi,
  (raw) => ingestInstructors(raw, {
    countries: useCountryStore.getState().items,
    languages: useLanguageStore.getState().items,
    partners: usePartnerStore.getState().items,
    clientExecutives: useClientExecutiveStore.getState().items,
  }),
  async () => {
    await Promise.all([
      useCountryStore.getState().loadAllOrThrow(),
      useLanguageStore.getState().loadAllOrThrow(),
      usePartnerStore.getState().loadAllOrThrow(),
      useClientExecutiveStore.getState().loadAllOrThrow()
    ]);
  }
);

export const useKlassStore = createEntityStore(
  KlassApi,
  (raw) => ingestKlasses(raw, {
    instructors: useInstructorStore.getState().items,
    facilities: useFacilityStore.getState().items,
    languages: useLanguageStore.getState().items,
    countries: useCountryStore.getState().items,
    courses: useCourseStore.getState().items,
    clientExecutives: useClientExecutiveStore.getState().items,
    tss: useTrainingSpecialistStrategicStore.getState().items,
  }),
  async () => {
    // Klasses are heavy; guarantee all dictionaries are loaded before ingestion
    await Promise.all([
      useInstructorStore.getState().loadAllOrThrow(),
      useFacilityStore.getState().loadAllOrThrow(),
      useLanguageStore.getState().loadAllOrThrow(),
      useCountryStore.getState().loadAllOrThrow(),
      useCourseStore.getState().loadAllOrThrow(),
      useClientExecutiveStore.getState().loadAllOrThrow(),
      useTrainingSpecialistStrategicStore.getState().loadAllOrThrow()
    ]);
  },
  15, // Drops from memory after 15 minutes of no views
  // ENTITY_STORE_PAGE_SIZE, which most stores take, would truncate the class window, and it would
  // do it silently - the list would simply be missing its tail.
  { size: KLASS_STORE_PAGE_SIZE }
);

/**
 * Pipeline requests, in their own store rather than in the class store: a request is not a class,
 * it has no offering id, and `/pipelineclass` is a separate collection from `/class`.
 *
 * Unlike the class store it takes no date window - a request has no start date to window by, and
 * one left pending for months is exactly the row somebody is looking for - so it holds the whole
 * collection like the reference stores do.
 */
export const usePipelineKlassStore = createEntityStore(
  PipelineKlassApi,
  (raw) => ingestPipelineKlasses(raw, {
    instructors: useInstructorStore.getState().items,
    languages: useLanguageStore.getState().items,
    countries: useCountryStore.getState().items,
    courses: useCourseStore.getState().items,
    clientExecutives: useClientExecutiveStore.getState().items,
    tss: useTrainingSpecialistStrategicStore.getState().items,
  }),
  async () => {
    await Promise.all([
      useInstructorStore.getState().loadAllOrThrow(),
      useLanguageStore.getState().loadAllOrThrow(),
      useCountryStore.getState().loadAllOrThrow(),
      useCourseStore.getState().loadAllOrThrow(),
      useClientExecutiveStore.getState().loadAllOrThrow(),
      useTrainingSpecialistStrategicStore.getState().loadAllOrThrow()
    ]);
  },
  15
);